import React, { useEffect, useState } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  UsersIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import usersService, { User, UserFilters } from '../services/usersService';
import adminUsersService, { AdminUserFilters } from '../services/adminUsersService';
import { AdminUser, UserPermissions } from '../types';
import { useAuthStore } from '../stores/authStore';

interface UserModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}

const UserModal: React.FC<UserModalProps> = ({ user, isOpen, onClose }) => {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [labels, setLabels] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadUserDetails();
    }
  }, [isOpen, user]);

  const loadUserDetails = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const [profilesData, labelsData] = await Promise.all([
        usersService.getUserProfiles(user.user_id),
        usersService.getUserLabels(user.user_id),
      ]);
      setProfiles(profilesData);
      setLabels(labelsData);
    } catch (error) {
      console.error('Erro ao carregar detalhes do usuário:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'abandoned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'abandoned':
        return 'Abandonado';
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--bg-overlay)' }} onClick={onClose}></div>

        <div className="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" style={{ backgroundColor: 'var(--card-bg)' }}>
          <div className="px-4 pt-5 pb-4 sm:p-6 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Detalhes do Usuário</h3>
              <button
                onClick={onClose}
                className="text-2xl leading-none transition-colors"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-muted)';
                }}
              >
                ×
              </button>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Informações Básicas */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Email</label>
                    <p className="mt-1 text-sm" style={{ color: 'var(--text-primary)' }}>{user.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Status</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.activity_status)}`}>
                        {getStatusText(user.activity_status)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Data de Criação</label>
                    <p className="mt-1 text-sm" style={{ color: 'var(--text-primary)' }}>{formatDate(user.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Último Login</label>
                    <p className="mt-1 text-sm" style={{ color: 'var(--text-primary)' }}>{formatDate(user.last_sign_in_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Última Atividade</label>
                    <p className="mt-1 text-sm" style={{ color: 'var(--text-primary)' }}>{formatDate(user.last_activity)}</p>
                  </div>
                </div>

                {/* Estatísticas */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Perfis Salvos</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{user.profiles_saved}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Labels Criadas</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{user.labels_created}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Notas Adicionadas</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{user.notes_added}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Reviews</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{user.company_reviews}</p>
                  </div>
                </div>

                {/* Labels */}
                <div>
                  <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Labels Criadas ({labels.length})</h4>
                  <div className="flex flex-wrap gap-2">
                    {labels.length > 0 ? (
                      labels.map((label) => (
                        <span
                          key={label.id}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${label.color}20`,
                            color: label.color,
                            border: `1px solid ${label.color}40`,
                          }}
                        >
                          {label.name}
                        </span>
                      ))
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Nenhuma label criada</p>
                    )}
                  </div>
                </div>

                {/* Perfis Recentes */}
                <div>
                  <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Perfis Salvos Recentes ({profiles.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {profiles.length > 0 ? (
                      profiles.slice(0, 10).map((profile) => (
                        <div key={profile.id} className="flex items-center space-x-3 p-2 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                          {profile.linkedin_profiles?.avatar && (
                            <img
                              src={profile.linkedin_profiles.avatar}
                              alt={profile.linkedin_profiles.name}
                              className="w-8 h-8 rounded-full"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                              {profile.linkedin_profiles?.name || 'Nome não disponível'}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                              {formatDate(profile.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Nenhum perfil salvo</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type UserType = 'extension' | 'admin';

export const Users: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [userType, setUserType] = useState<UserType>('extension');
  
  // Extension users
  const [extensionUsers, setExtensionUsers] = useState<User[]>([]);
  const [selectedExtensionUser, setSelectedExtensionUser] = useState<User | null>(null);
  
  // Admin users
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [selectedAdminUser, setSelectedAdminUser] = useState<AdminUser | null>(null);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Paginação
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filtros
  const [search, setSearch] = useState('');
  const [activityFilter, setActivityFilter] = useState<'all' | 'active' | 'inactive' | 'abandoned'>('all');
  const [roleFilter, setRoleFilter] = useState<'all' | 'superadmin' | 'admin' | 'user'>('all');

  useEffect(() => {
    if (userType === 'extension') {
      loadExtensionUsers();
    } else {
      loadAdminUsers();
    }
  }, [page, search, activityFilter, roleFilter, userType]);

  const loadExtensionUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: UserFilters = {};
      if (search) {
        filters.search = search;
      }
      if (activityFilter !== 'all') {
        filters.activityStatus = activityFilter;
      }

      const response = await usersService.getUsers(filters, { page, limit });
      setExtensionUsers(response.users);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Erro ao carregar usuários da extensão:', err);
      setError('Erro ao carregar usuários. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: AdminUserFilters = {};
      if (search) {
        filters.search = search;
      }
      if (roleFilter !== 'all') {
        filters.role = roleFilter;
      }

      const response = await adminUsersService.getAdminUsers(filters, { page, limit });
      setAdminUsers(response.users);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Erro ao carregar usuários administrativos:', err);
      setError('Erro ao carregar usuários administrativos. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewExtensionUser = (user: User) => {
    setSelectedExtensionUser(user);
    setIsModalOpen(true);
  };

  const handleEditAdminUser = (user: AdminUser) => {
    setSelectedAdminUser(user);
    setIsCreateMode(false);
    setIsAdminModalOpen(true);
  };

  const handleCreateAdminUser = () => {
    setSelectedAdminUser(null);
    setIsCreateMode(true);
    setIsAdminModalOpen(true);
  };

  const handleDeleteAdminUser = async (userId: string) => {
    if (!confirm('Tem certeza que deseja deletar este usuário administrativo?')) {
      return;
    }

    try {
      await adminUsersService.deleteAdminUser(userId);
      loadAdminUsers();
    } catch (err) {
      console.error('Erro ao deletar usuário:', err);
      alert('Erro ao deletar usuário');
    }
  };

  const handleExport = async () => {
    try {
      const filters: UserFilters = {};
      if (search) filters.search = search;
      if (activityFilter !== 'all') filters.activityStatus = activityFilter;

      const data = await usersService.exportUsers(filters);
      
      // Criar CSV
      const headers = ['Email', 'Status', 'Perfis Salvos', 'Labels', 'Criado em', 'Última Atividade'];
      const rows = data.map((u) => [
        u.email,
        u.activity_status,
        u.profiles_saved.toString(),
        u.labels_created.toString(),
        new Date(u.created_at).toLocaleDateString('pt-BR'),
        u.last_activity ? new Date(u.last_activity).toLocaleDateString('pt-BR') : 'N/A',
      ]);

      const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `usuarios_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (err) {
      console.error('Erro ao exportar usuários:', err);
      alert('Erro ao exportar usuários');
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'abandoned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'abandoned':
        return 'Abandonado';
      default:
        return status;
    }
  };

  const canManageAdminUsers = currentUser?.role === 'superadmin' || 
    (currentUser?.permissions as UserPermissions)?.can_manage_users;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-600">
            {userType === 'extension' 
              ? 'Gerenciar usuários da extensão' 
              : 'Gerenciar usuários administrativos'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {userType === 'extension' && (
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Exportar
            </button>
          )}
          {userType === 'admin' && canManageAdminUsers && (
            <button
              onClick={handleCreateAdminUser}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-linkedin-600 hover:bg-linkedin-700"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Novo Usuário Admin
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setUserType('extension');
              setPage(1);
              setSearch('');
              setActivityFilter('all');
              setRoleFilter('all');
            }}
            className={`${
              userType === 'extension'
                ? 'border-linkedin-500 text-linkedin-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            <UsersIcon className="h-5 w-5 inline mr-2" />
            Usuários da Extensão
          </button>
          <button
            onClick={() => {
              setUserType('admin');
              setPage(1);
              setSearch('');
              setActivityFilter('all');
              setRoleFilter('all');
            }}
            className={`${
              userType === 'admin'
                ? 'border-linkedin-500 text-linkedin-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            <UserGroupIcon className="h-5 w-5 inline mr-2" />
            Usuários Administrativos
          </button>
        </nav>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
              />
            </div>
          </div>
          {userType === 'extension' ? (
            <select
              value={activityFilter}
              onChange={(e) => {
                setActivityFilter(e.target.value as any);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
              <option value="abandoned">Abandonados</option>
            </select>
          ) : (
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value as any);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
            >
              <option value="all">Todos os níveis</option>
              <option value="superadmin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="card">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Carregando usuários...</p>
          </div>
        ) : (userType === 'extension' ? extensionUsers.length === 0 : adminUsers.length === 0) ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Nenhum usuário encontrado</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Usuário
                    </th>
                    {userType === 'extension' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Perfis
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Labels
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Criado em
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Última Atividade
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Nível
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                          Último Login
                        </th>
                      </>
                    )}
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: 'var(--card-bg)' }}>
                  {userType === 'extension' ? (
                    extensionUsers.map((user) => (
                      <tr 
                        key={user.user_id} 
                        className="hover:bg-gray-50"
                        style={{ 
                          borderTopColor: 'var(--border-primary)',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                              <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                                {user.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.activity_status)}`}>
                            {getStatusText(user.activity_status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                          {user.profiles_saved}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                          {user.labels_created}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {formatDate(user.last_activity)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewExtensionUser(user)}
                            className="text-linkedin-600 hover:text-linkedin-900"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    adminUsers.map((user) => {
                      const getRoleBadgeColor = (role: string) => {
                        switch (role) {
                          case 'superadmin':
                            return 'bg-purple-100 text-purple-800';
                          case 'admin':
                            return 'bg-blue-100 text-blue-800';
                          case 'user':
                            return 'bg-gray-100 text-gray-800';
                          default:
                            return 'bg-gray-100 text-gray-800';
                        }
                      };
                      const getRoleLabel = (role: string) => {
                        switch (role) {
                          case 'superadmin':
                            return 'Super Admin';
                          case 'admin':
                            return 'Admin';
                          case 'user':
                            return 'User';
                          default:
                            return role;
                        }
                      };
                      return (
                        <tr 
                          key={user.id} 
                          className="hover:bg-gray-50"
                          style={{ 
                            borderTopColor: 'var(--border-primary)',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                                <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {user.email.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.email}</div>
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{user.region || 'global'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                              {getRoleLabel(user.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {user.is_active ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Ativo
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Inativo
                                </span>
                              )}
                              {user.is_blocked && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Bloqueado
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {user.last_login
                              ? new Date(user.last_login).toLocaleDateString('pt-BR')
                              : 'Nunca'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {canManageAdminUsers && (
                                <>
                                  <button
                                    onClick={() => handleEditAdminUser(user)}
                                    className="text-linkedin-600 hover:text-linkedin-900"
                                    title="Editar"
                                  >
                                    <PencilIcon className="h-5 w-5" />
                                  </button>
                                  {user.id !== currentUser?.id && (
                                    <button
                                      onClick={() => handleDeleteAdminUser(user.id)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Deletar"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{(page - 1) * limit + 1}</span> até{' '}
                      <span className="font-medium">{Math.min(page * limit, total)}</span> de{' '}
                      <span className="font-medium">{total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= page - 1 && pageNum <= page + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === page
                                  ? 'z-10 bg-linkedin-50 border-linkedin-500 text-linkedin-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === page - 2 || pageNum === page + 2) {
                          return <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                        }
                        return null;
                      })}
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Detalhes - Extension User */}
      <UserModal
        user={selectedExtensionUser}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedExtensionUser(null);
        }}
      />

      {/* Modal de Admin User */}
      {isAdminModalOpen && (
        <AdminUserModal
          user={isCreateMode ? null : selectedAdminUser}
          isOpen={isAdminModalOpen}
          onClose={() => {
            setIsAdminModalOpen(false);
            setSelectedAdminUser(null);
            setIsCreateMode(false);
          }}
          onSave={() => {
            if (userType === 'admin') {
              loadAdminUsers();
            }
          }}
        />
      )}
    </div>
  );
};

// AdminUserModal Component
interface AdminUserModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AdminUserModal: React.FC<AdminUserModalProps> = ({ user, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    role: 'admin' as 'superadmin' | 'admin' | 'user',
    region: 'global',
    is_active: true,
    is_blocked: false,
    permissions: {
      can_manage_users: false,
      can_view_logs: true,
      can_manage_settings: false,
      can_view_analytics: true,
      can_export_data: false,
      can_manage_backups: false,
      can_view_security_logs: false,
    } as UserPermissions,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        role: user.role,
        region: user.region || 'global',
        is_active: user.is_active,
        is_blocked: user.is_blocked,
        permissions: (user.permissions as UserPermissions) || {
          can_manage_users: false,
          can_view_logs: true,
          can_manage_settings: false,
          can_view_analytics: true,
          can_export_data: false,
          can_manage_backups: false,
          can_view_security_logs: false,
        },
      });
    } else {
      setFormData({
        email: '',
        role: 'admin',
        region: 'global',
        is_active: true,
        is_blocked: false,
        permissions: {
          can_manage_users: false,
          can_view_logs: true,
          can_manage_settings: false,
          can_view_analytics: true,
          can_export_data: false,
          can_manage_backups: false,
          can_view_security_logs: false,
        },
      });
    }
    setError(null);
  }, [user, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (user) {
        await adminUsersService.updateAdminUser(user.id, formData);
      } else {
        await adminUsersService.createAdminUser(formData);
      }
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermissionChange = (key: keyof UserPermissions, value: boolean) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [key]: value,
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--bg-overlay)' }} onClick={onClose}></div>

        <div className="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" style={{ backgroundColor: 'var(--card-bg)' }}>
          <form onSubmit={handleSubmit}>
            <div className="px-4 pt-5 pb-4 sm:p-6" style={{ backgroundColor: 'var(--card-bg)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                  {user ? 'Editar Usuário Administrativo' : 'Novo Usuário Administrativo'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-2xl leading-none transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-muted)';
                  }}
                >
                  ×
                </button>
              </div>

              {error && (
                <div className="mb-4 border border-red-200 rounded-lg p-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
                    style={{ 
                      backgroundColor: 'var(--input-bg)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--input-border)'
                    }}
                    disabled={!!user}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Nível de Acesso *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
                    style={{ 
                      backgroundColor: 'var(--input-bg)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--input-border)'
                    }}
                  >
                    <option value="superadmin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="user">User (sem acesso ao sistema)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Região</label>
                  <input
                    type="text"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
                    style={{ 
                      backgroundColor: 'var(--input-bg)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--input-border)'
                    }}
                    placeholder="global"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-linkedin-600 focus:ring-linkedin-500"
                    />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Usuário Ativo</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_blocked}
                      onChange={(e) => setFormData({ ...formData, is_blocked: e.target.checked })}
                      className="rounded border-gray-300 text-linkedin-600 focus:ring-linkedin-500"
                    />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Usuário Bloqueado</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Permissões</label>
                  <div className="space-y-2 border rounded-lg p-4" style={{ borderColor: 'var(--border-primary)' }}>
                    {Object.entries(formData.permissions).map(([key, value]) => (
                      <label key={key} className="flex items-center justify-between">
                        <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) =>
                            handlePermissionChange(key as keyof UserPermissions, e.target.checked)
                          }
                          className="rounded border-gray-300 text-linkedin-600 focus:ring-linkedin-500"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-linkedin-600 text-base font-medium text-white hover:bg-linkedin-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-linkedin-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {isLoading ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-lg border shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-linkedin-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                style={{ 
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-primary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
